import { useCallback } from 'react';
import { getIn } from 'formik';
import { Grid } from '@mui/material';

import { FormAutocompleteField, FormPhoneField, FormTextField } from '../../components';
import { US_STATE_OPTIONS } from '../../../../constants';
import {
  CAREGIVER_TYPE_OPTIONS,
  CUSTODY_TYPE_OPTIONS,
  FAMILY_RELATIONSHIP_TO_CLIENT_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  MEMBER_ROLE_OPTIONS,
  MEMBER_ROLE_PARENT_NOT_CAREGIVER,
} from '../constants';

import type { FamilyMemberFieldName, FamilyMemberFormValues } from '../types';
import type { ParentAndCaregiverFieldsProps } from './types';

export default function ParentAndCaregiverFields({
  formik,
  disabled,
  readOnly,
  saveField,
}: ParentAndCaregiverFieldsProps) {
  const isParentNotCaregiver =
    getIn(formik.values, 'member_role') === MEMBER_ROLE_PARENT_NOT_CAREGIVER;

  const handleMemberRoleChange = useCallback(
    (name: FamilyMemberFieldName, value: FamilyMemberFormValues[FamilyMemberFieldName]) => {
      saveField(name, value);
      if (value === MEMBER_ROLE_PARENT_NOT_CAREGIVER) {
        void formik.setFieldValue('caregiver_type', null);
        void formik.setFieldTouched('caregiver_type', false);
      }
    },
    [formik, saveField],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={4}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          label="First & last name"
          name="full_name"
          onBlur={saveField}
          placeholder="Enter full name"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          label="Role"
          name="member_role"
          onChange={handleMemberRoleChange}
          options={MEMBER_ROLE_OPTIONS}
          placeholder="Select a role"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <FormAutocompleteField
          disabled={disabled || isParentNotCaregiver}
          formik={formik}
          label="Primary/secondary caregiver"
          name="caregiver_type"
          onChange={saveField}
          options={CAREGIVER_TYPE_OPTIONS}
          placeholder="Select a caregiver type"
          readOnly={readOnly}
          required={!isParentNotCaregiver}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          label="Relationship to child"
          name="relationship_to_client"
          onChange={saveField}
          options={FAMILY_RELATIONSHIP_TO_CLIENT_OPTIONS}
          placeholder="Select a relationship"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          label="Child custody"
          name="custody_type"
          onChange={saveField}
          options={CUSTODY_TYPE_OPTIONS}
          placeholder="Select a custody type"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          label="Marital status"
          name="marital_status"
          onChange={saveField}
          options={MARITAL_STATUS_OPTIONS}
          placeholder="Select a marital status"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          label="Job"
          name="job_title"
          onBlur={saveField}
          placeholder="Enter job title"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          label="Email"
          name="email"
          onBlur={saveField}
          placeholder="Enter email"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormPhoneField
          disabled={disabled}
          formik={formik}
          label="Phone"
          name="phone"
          onBlur={saveField}
          placeholder="Enter 10 digit phone number"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          label="Street address line one"
          name="street_address_line_one"
          onBlur={saveField}
          placeholder="Enter street address"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          label="Address line two (Apartment/unit)"
          name="street_address_line_two"
          onBlur={saveField}
          placeholder="Enter apartment/unit"
          readOnly={readOnly}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          label="City"
          name="street_address_city"
          onBlur={saveField}
          placeholder="Enter city"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormAutocompleteField
          disabled={disabled}
          formik={formik}
          label="State"
          name="street_address_state"
          onChange={saveField}
          options={US_STATE_OPTIONS}
          placeholder="Select state"
          readOnly={readOnly}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <FormTextField
          disabled={disabled}
          formik={formik}
          label="Zip code"
          name="street_address_zip"
          onBlur={saveField}
          placeholder="Enter 5 digit zip code"
          readOnly={readOnly}
          required
        />
      </Grid>
    </Grid>
  );
}
